import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { MainLayout } from "@/components/layout";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import LeadsPage from "@/pages/LeadsPage";
import CustomersPage from "@/pages/CustomersPage";
import InspectionsPage from "@/pages/InspectionsPage";
import LoansPage from "@/pages/LoansPage";
import FinancePage from "@/pages/FinancePage";
import SettingsPage from "@/pages/SettingsPage";
import ServicePackagesPage from "@/pages/ServicePackagesPage";
import ServiceLeadsPage from "@/pages/ServiceLeadsPage";
import ServiceOpsPage from "@/pages/ServiceOpsPage";
import TrackerHealthPage from "@/pages/TrackerHealthPage";
import HRModulePage from "@/pages/HRModulePage";
import NotificationConfigPage from "@/pages/NotificationConfigPage";
import AdsManagementPage from "@/pages/AdsManagementPage";
import MonitorPage from "@/pages/ErrorLogsPage";
import InspectionReportPage from "@/pages/InspectionReportPage";
import CustomerReportPage from "@/pages/CustomerReportPage";
import PreviewReportPage from "@/pages/PreviewReportPage";
import UnifiedReportPage from "@/pages/UnifiedReportPage";
import MetaOAuthCallback from "@/pages/MetaOAuthCallback";
import MechanicAppTestPage from "@/pages/MechanicAppTestPage";
import AdminPasswordReset from "@/pages/AdminPasswordReset";
import SystemDiagnostics from "@/pages/SystemDiagnostics";
import SheerdriveReportPreview from "@/pages/SheerdriveReportPreview";
import SheerdriveReport from "@/pages/SheerdriveReport";
import PdfDebugPage from "@/pages/PdfDebugPage";
import PaymentReconcile from "@/pages/PaymentReconcile";
import DealersPage from "@/pages/DealersPage";
import ObdSdkDashboard from "@/pages/ObdSdkDashboard";
import MfcwlDashboard from "@/pages/MfcwlDashboard";
import MfcwlPublicReportPage from "@/pages/MfcwlPublicReportPage";
import MechanicAppDownload from "@/pages/MechanicAppDownload";
import { pickLandingRoute } from "@/utils/landingRoute";

// Smart redirect based on role + visible tabs.
// Centralised in /utils/landingRoute.js so LoginPage.jsx stays in sync.
const SmartRedirect = () => {
  const { user, visibleTabs, loading } = useAuth();
  if (loading) return null;
  const route = pickLandingRoute({ user, visibleTabs });
  return <Navigate to={route} replace />;
};

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          
          {/* Meta OAuth Callback - Public route for popup */}
          <Route path="/meta-oauth-callback" element={<MetaOAuthCallback />} />
          
          {/* Mechanic App Test Page - Debug time formatting */}
          <Route path="/mechanic-test" element={<MechanicAppTestPage />} />

          {/* Public mechanic-app download page (QR/sticker target: /m) */}
          <Route path="/m" element={<MechanicAppDownload />} />
          
          {/* Admin Password Reset - Only works when REACT_APP_ENABLE_PASSWORD_RESET=true */}
          <Route path="/admin/reset-password" element={<AdminPasswordReset />} />
          
          {/* System Diagnostics - Check backend health and connectivity */}
          <Route path="/admin/diagnostics" element={<SystemDiagnostics />} />
          
          {/* UNIFIED REPORT PAGE - Single source of truth for customer views */}
          {/* Customer Report Access - Public route with OTP verification */}
          <Route path="/r/:id" element={<UnifiedReportPage />} />
          <Route path="/report/:id" element={<UnifiedReportPage />} />
          <Route path="/portal/:id" element={<UnifiedReportPage />} />
          
          {/* Admin Preview Report - Uses PreviewReportPage (battle-tested) */}
          <Route path="/preview-report/:id" element={<PreviewReportPage />} />
          
          {/* Legacy routes for backward compatibility */}
          <Route path="/inspection-report/:id" element={<InspectionReportPage />} />
          <Route path="/inspection-report" element={<InspectionReportPage />} />
          
          {/* Sheerdrive Report Preview - Public sample view */}
          <Route path="/sheerdrive-report-preview" element={<SheerdriveReportPreview />} />
          {/* Sheerdrive Report - Dynamic per inspection */}
          <Route path="/sheerdrive-report/:inspectionId" element={<SheerdriveReport />} />

          {/* MFCWL public tokenized web report */}
          <Route path="/mfcwl/report/:id" element={<MfcwlPublicReportPage />} />
          
          {/* Protected Routes */}
          <Route element={<MainLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/leads" element={<LeadsPage />} />
            <Route path="/dealers" element={<DealersPage />} />
            <Route path="/customers" element={<Navigate to="/customers/inspections" replace />} />
            <Route path="/customers/inspections" element={<CustomersPage />} />
            <Route path="/customers/service" element={<CustomersPage />} />
            <Route path="/inspections" element={<InspectionsPage />} />
            <Route path="/loans" element={<LoansPage />} />
            <Route path="/ad-analytics" element={<AdsManagementPage />} />
            <Route path="/hr" element={<HRModulePage />} />
            <Route path="/hr/notifications" element={<Navigate to="/system-monitor" replace />} />
            <Route path="/finance" element={<FinancePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/service-packages" element={<ServicePackagesPage />} />
            <Route path="/service-leads" element={<ServiceLeadsPage />} />
            <Route path="/service-ops" element={<ServiceOpsPage />} />
            {/* Old standalone path → redirect into the unified Monitor tab.
                Bookmarks & previously-shared links keep working. */}
            <Route path="/tracker-health" element={<Navigate to="/system-monitor?tab=tracker-health" replace />} />
            <Route path="/system-monitor" element={<MonitorPage />} />
            <Route path="/settings/notifications" element={<Navigate to="/system-monitor" replace />} />
            <Route path="/admin/pdf-debug" element={<PdfDebugPage />} />
            <Route path="/admin/payment-reconcile" element={<PaymentReconcile />} />
            <Route path="/obd-sdk" element={<ObdSdkDashboard />} />
            <Route path="/mfcwl" element={<MfcwlDashboard />} />
            {/* Redirect old /admin to /hr */}
            <Route path="/admin" element={<Navigate to="/hr" replace />} />
            {/* /reports is referenced by visible_tabs but no Reports page
                exists yet — redirect to /dashboard so stale bookmarks /
                cached redirects from the 2026-06-12 incident don't land
                users on a blank page. */}
            <Route path="/reports" element={<Navigate to="/dashboard" replace />} />
          </Route>

          {/* Default redirect */}
          <Route path="/" element={<SmartRedirect />} />
          <Route path="*" element={<SmartRedirect />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
